import type { CompanyDetailsUpdate } from '@pulsify/shared/dto'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { businessApi } from '~/entities/business/api/business'
import { sessionHasDashboardRole, useSupabaseSession } from '~/entities/session/model/use-auth'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { storageManager } from '~/shared/lib/storage-manager'
import { MINUTES } from '~/shared/lib/time-constants'

/**
 * Returns the current Business record (id, companyName, legalName) via
 * `/api/businesses/me`. Listing-shaped fields (display name shown on the
 * public site, description, location, hours, ...) are NOT on this shape -
 * use {@link usePrimaryListing} from hooks/use-listings for that.
 *
 * The Business=Listing alias that used to bundle them together produced
 * the "My Business" placeholder + bogus location defaults; splitting at
 * the type level forces call sites to declare which one they actually
 * want.
 */
export function useCurrentBusiness() {
  const session = useSupabaseSession()
  // Admins have no "my business": getMe() only resolves when a specific
  // business is being viewed (it then sends ?businessId=). A pure admin would
  // otherwise hit /business-users/me with no businessId → 400
  // AdminRequiresBusinessId, so gate the query off until one is selected.
  const isAdmin = storageManager.isAdmin()
  const adminViewingBusinessId = storageManager.getAdminViewingBusinessId()
  return useQuery({
    queryKey: adminViewingBusinessId
      ? queryKey(QueryKey.BUSINESS_CURRENT, adminViewingBusinessId)
      : queryKey(QueryKey.BUSINESS_CURRENT),
    queryFn: () => businessApi.getMe(),
    enabled:
      !!session && sessionHasDashboardRole(session) && (!isAdmin || !!adminViewingBusinessId),
    staleTime: 5 * MINUTES,
    retry: false,
  })
}

/** Saves the company details of the current business and refreshes everything that shows them. */
export function useUpdateCompanyDetails(businessId: string | null | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CompanyDetailsUpdate) =>
      businessApi.updateCompanyDetails(businessId ?? '', input),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.BUSINESS_CURRENT) }),
        queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.BILLING_ENTITIES) }),
      ])
    },
  })
}
